import api from '@/lib/axios';
import type { PaginatedResult } from '@/components/data-table';
import { downloadExport, ExportFormat } from '@/services/school.api';

export type GuardianAttendanceType = 0 | 1;

export interface GuardianAttendanceGuardian {
  id: string;
  firstName: string | null;
  username: string;
}

export interface GuardianAttendanceRecord {
  id: string;
  legacyId: number | null;
  guardianId: string;
  guardian?: GuardianAttendanceGuardian;
  schoolId: string;
  school?: { id: string; name: string };
  type: GuardianAttendanceType;
  date: string;
  addedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GuardianAttendanceSummaryRow {
  guardianId: string;
  schoolId: string;
  schoolName: string;
  guardianName: string;
  present: number;
  absent: number;
  total: number;
  /** True when this guardian has zero attendance rows at all in the date range — distinct from an explicit absent mark. */
  notMarked: boolean;
}

export interface GuardianOption {
  id: string;
  name: string;
}

export interface ListGuardianAttendanceParams {
  page?: number;
  limit?: number;
  schoolId?: string;
  date?: string;
  guardianId?: string;
  type?: GuardianAttendanceType;
}

export interface SummaryGuardianAttendanceParams {
  schoolId?: string;
  startDate?: string;
  endDate?: string;
}

export interface CreateGuardianAttendancePayload {
  schoolId?: string;
  guardianId: string;
  type: GuardianAttendanceType;
  date: string;
}

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export const GuardianAttendanceApi = {
  list: async (params?: ListGuardianAttendanceParams): Promise<PaginatedResult<GuardianAttendanceRecord>> =>
    unwrap(await api.get('/guardian-attendance', { params })),
  summary: async (params?: SummaryGuardianAttendanceParams): Promise<GuardianAttendanceSummaryRow[]> =>
    unwrap(await api.get('/guardian-attendance/summary', { params })),
  exportSummary: (format: ExportFormat, params?: SummaryGuardianAttendanceParams) =>
    downloadExport('/guardian-attendance/summary/export', 'parent-attendance-summary', format, params as Record<string, unknown>),
  guardiansForSchool: async (params?: { schoolId?: string; search?: string }): Promise<GuardianOption[]> =>
    unwrap(await api.get('/guardian-attendance/guardians', { params })),
  create: async (data: CreateGuardianAttendancePayload): Promise<GuardianAttendanceRecord> =>
    unwrap(await api.post('/guardian-attendance', data)),
};
